import { Activity, Bone, Dumbbell, FileCheck2, ScanLine } from "lucide-react";
import deporteLogo from "../assets/deporte-logo.png";
import firmaPaola from "../assets/firma-paola.png";
import { toFiniteNumber } from "../lib/dexaCalculations.js";

const regionLabels = {
  leftArm: "Brazo izquierdo", rightArm: "Brazo derecho", trunk: "Tronco",
  leftLeg: "Pierna izquierda", rightLeg: "Pierna derecha", head: "Cabeza",
};
const boneLabels = {
  leftArm: "Brazo izquierdo", rightArm: "Brazo derecho", leftRibs: "Costillas izquierdas",
  rightRibs: "Costillas derechas", thoracicSpine: "Columna dorsal", lumbarSpine: "Columna lumbar",
  pelvis: "Pelvis", leftLeg: "Pierna izquierda", rightLeg: "Pierna derecha", head: "Cabeza",
};

function format(value, digits = 1, suffix = "") {
  const number = toFiniteNumber(value);
  return number === null ? "-" : `${number.toLocaleString("es-CO", {
    minimumFractionDigits: digits, maximumFractionDigits: digits,
  })}${suffix}`;
}

function kilograms(value, digits = 1) {
  const number = toFiniteNumber(value);
  return number === null ? "-" : format(number / 1000, digits, " kg");
}

function dateLabel(value) {
  if (!value) return "Fecha pendiente";
  const date = new Date(`${value}T12:00:00`);
  return Number.isNaN(date.valueOf()) ? value : date.toLocaleDateString("es-CO", {
    day: "numeric", month: "long", year: "numeric",
  });
}

function getVisual(record, role, key) {
  const page = record?.source?.pages?.find((item) => item.role === role);
  return page?.visuals?.[key] || page?.imageUrl || "";
}

function getVisualFrom(record, candidates) {
  for (const [role, key] of candidates) {
    const visual = getVisual(record, role, key);
    if (visual) return visual;
  }
  return "";
}

function Header({ comparison, page }) {
  const { currentRecord, previousRecord, records } = comparison;
  return (
    <header className={`dxa-report-header ${page > 1 ? "dxa-report-header--compact" : ""}`}>
      <div className="dxa-report-brand">
        <img src={deporteLogo} alt="Deporte · Ministerio del Deporte" />
        <div>
          <strong>Resultados comparativos de evaluación por DXA</strong>
          <small>Seguimiento longitudinal de composición corporal y densidad ósea</small>
          <small className="dxa-report-author">Elaborado por Nutricionista Paola Hoyos</small>
        </div>
      </div>
      <div className="dxa-report-page-label">Página {page} de 4</div>
      <dl className="dxa-report-patient">
        <div><dt>Paciente</dt><dd>{currentRecord.patient.name || "Paciente"}</dd></div>
        <div><dt>Valoración anterior</dt><dd>{dateLabel(previousRecord.exam.date)}</dd></div>
        <div><dt>Valoración actual</dt><dd>{dateLabel(currentRecord.exam.date)}</dd></div>
        <div><dt>Evaluaciones</dt><dd>{records.length}</dd></div>
        <div><dt>Estatura actual</dt><dd>{format(currentRecord.patient.heightCm, 1, " cm")}</dd></div>
        <div><dt>Peso actual</dt><dd>{format(currentRecord.patient.scaleWeightKg, 1, " kg")}</dd></div>
        <div><dt>Identificación</dt><dd>{currentRecord.patient.patientId || "-"}</dd></div>
        <div><dt>Equipo</dt><dd>{[currentRecord.exam.vendor, currentRecord.exam.model].filter(Boolean).join(" · ") || "-"}</dd></div>
      </dl>
    </header>
  );
}

function Footer({ comparison, page }) {
  return (
    <footer className="dxa-report-footer">
      <span>Informe DXA comparativo</span>
      <strong>{comparison.currentRecord.patient.name || "Paciente"}</strong>
      <span>{dateLabel(comparison.currentRecord.exam.date)} · {page}/4</span>
    </footer>
  );
}

function SectionBand({ icon: Icon, eyebrow, title }) {
  return <div className="dxa-section-band"><Icon /><div><span>{eyebrow}</span><h2>{title}</h2></div></div>;
}

function Interpretation({ title, children, tone = "green" }) {
  return <div className={`dxa-interpretation-box dxa-interpretation-box--${tone}`}><strong>{title}</strong><p>{children}</p></div>;
}

function Scan({ src, title, className = "" }) {
  return (
    <figure className={`dxa-device-visual ${className}`}>
      <figcaption>{title}</figcaption>
      {src ? <img src={src} alt={title} /> : <div className="dxa-device-visual__fallback"><ScanLine /></div>}
    </figure>
  );
}

function Delta({ comparison, unit = "", digits = 1 }) {
  if (!comparison || comparison.delta === null) return <span className="comparison-delta">Sin comparación</span>;
  if (comparison.direction === "stable") return <span className="comparison-delta comparison-delta--stable">Sin cambio</span>;
  return (
    <span className={`comparison-delta comparison-delta--${comparison.direction}`}>
      <b className="comparison-delta__arrow" aria-hidden="true">{comparison.direction === "up" ? "↑" : "↓"}</b>
      {format(Math.abs(comparison.delta), digits, unit)}
    </span>
  );
}

function TotalComparison({ comparison }) {
  const previous = comparison.previousRecord.composition.total;
  const current = comparison.currentRecord.composition.total;
  const rows = [
    ["Grasa corporal", previous.fatPercent, current.fatPercent, comparison.total.fatPercent, "%", 1, "orange"],
    ["Masa total DXA", previous.totalMassG, current.totalMassG, comparison.total.totalMassG, " g", 0, "neutral"],
    ["Tejido graso", previous.fatMassG, current.fatMassG, comparison.total.fatMassG, " g", 0, "yellow"],
    ["Tejido magro", previous.leanMassG, current.leanMassG, comparison.total.leanMassG, " g", 0, "rose"],
    ["Contenido mineral óseo", previous.bmcG, current.bmcG, comparison.total.bmcG, " g", 1, "blue"],
    ["Índice de masa corporal (IMC)", comparison.previousRecord.composition.indices.bmi, comparison.currentRecord.composition.indices.bmi, comparison.indices.bmi, " kg/m²", 1, "green"],
  ];
  return (
    <div className="comparison-primary-results">
      <div className="comparison-primary-results__head"><span>Variable</span><span>{dateLabel(comparison.previousRecord.exam.date)}</span><span>{dateLabel(comparison.currentRecord.exam.date)}</span><span>Cambio</span></div>
      {rows.map(([label, before, after, delta, unit, digits, tone]) => (
        <div className={`comparison-primary-row comparison-primary-row--${tone}`} key={label}>
          <strong>{label}</strong><span>{format(before, digits, unit)}</span><span>{format(after, digits, unit)}</span><Delta comparison={delta} unit={unit} digits={digits} />
        </div>
      ))}
    </div>
  );
}

function RegionalTable({ comparison }) {
  const before = comparison.previousRecord.composition.regions;
  const after = comparison.currentRecord.composition.regions;
  return (
    <div className="report-table-wrap comparison-wide-table"><table className="report-table report-table--dense">
      <thead><tr><th rowSpan="2">Región</th>{["Grasa kg", "Magra kg", "CMO kg", "Total kg", "Grasa %"].map((label) => <th colSpan="2" key={label}>{label}</th>)}</tr>
      <tr>{Array.from({ length: 5 }).flatMap((_, index) => [<th key={`a-${index}`}>Anterior</th>, <th key={`c-${index}`}>Actual</th>])}</tr></thead>
      <tbody>{Object.entries(regionLabels).map(([key, label]) => <tr key={key}>
        <th>{label}</th>
        <td>{kilograms(before[key]?.fatMassG, 3)}</td><td>{kilograms(after[key]?.fatMassG, 3)}</td>
        <td>{kilograms(before[key]?.leanMassG, 3)}</td><td>{kilograms(after[key]?.leanMassG, 3)}</td>
        <td>{kilograms(before[key]?.bmcG, 3)}</td><td>{kilograms(after[key]?.bmcG, 3)}</td>
        <td>{kilograms(before[key]?.totalMassG, 3)}</td><td>{kilograms(after[key]?.totalMassG, 3)}</td>
        <td>{format(before[key]?.fatPercent, 1, "%")}</td><td>{format(after[key]?.fatPercent, 1, "%")}</td>
      </tr>)}</tbody>
    </table></div>
  );
}

function IndicesTable({ comparison, muscle = false }) {
  const before = comparison.previousRecord.composition.indices;
  const after = comparison.currentRecord.composition.indices;
  const rows = muscle ? [
    ["Índice de masa magra / estatura² (IMM)", "lmi", "kg/m²", 2, "lmiPercentileYoung", "lmiPercentileAgeMatched"],
    ["Índice de masa magra apendicular / estatura² (IMMA)", "almi", "kg/m²", 2, "almiPercentileYoung", "almiPercentileAgeMatched"],
  ] : [
    ["Índice de masa grasa (IMG)", "fmi", "kg/m²", 2, "fmiPercentileYoung", "fmiPercentileAgeMatched"],
    ["Relación androide / ginoide", "androidGynoidRatio", "", 2],
    ["Grasa tronco / piernas", "trunkLegFatRatio", "", 2, "trunkLegFatPercentileYoung", "trunkLegFatPercentileAgeMatched"],
    ["Grasa tronco / extremidades", "trunkExtremityFatRatio", "", 2, "trunkExtremityFatPercentileYoung", "trunkExtremityFatPercentileAgeMatched"],
    ["Tejido adiposo visceral · masa", "vatMassG", " g", 0],
    ["Tejido adiposo visceral · área", "vatAreaCm2", " cm²", 1],
  ];
  return <div className="report-table-wrap"><table className="report-table report-table--dense comparison-index-table">
    <thead><tr><th>Índice</th><th>Anterior</th><th>Actual</th><th>Cambio</th><th>Pctl. JN A/C</th><th>Pctl. EC A/C</th></tr></thead>
    <tbody>{rows.map(([label, key, unit, digits, gn, ec]) => <tr key={key}>
      <th>{label}</th><td>{format(before[key], digits, unit)}</td><td>{format(after[key], digits, unit)}</td>
      <td><Delta comparison={comparison.indices[key]} unit={unit} digits={digits} /></td>
      <td>{gn ? `${format(before[gn], 0)} / ${format(after[gn], 0)}` : "-"}</td>
      <td>{ec ? `${format(before[ec], 0)} / ${format(after[ec], 0)}` : "-"}</td>
    </tr>)}</tbody>
  </table></div>;
}

function BoneComparisonTable({ labels, before, after, comparisons, rows, includePercent = false }) {
  return <div className="report-table-wrap"><table className="report-table report-table--dense comparison-bone-table">
    <thead><tr><th>Región</th><th>Área A/C</th><th>CMO A/C</th><th>DMO A/C</th><th>T A/C</th><th>Z A/C</th>{includePercent ? <><th>RM A/C</th><th>EC A/C</th></> : null}<th>Lectura</th></tr></thead>
    <tbody>{rows.map((key) => {
      const oldItem = before[key]; const newItem = after[key]; const change = comparisons[key];
      return <tr key={key}><th>{labels[key] || key}</th>
        <td>{format(oldItem?.areaCm2, 2)} / {format(newItem?.areaCm2, 2)}</td>
        <td>{format(oldItem?.bmcG, 2)} / {format(newItem?.bmcG, 2)}</td>
        <td>{format(oldItem?.bmd, 3)} / {format(newItem?.bmd, 3)}</td>
        <td>{format(oldItem?.tScore, 1)} / {format(newItem?.tScore, 1)}</td>
        <td>{format(oldItem?.zScore, 1)} / {format(newItem?.zScore, 1)}</td>
        {includePercent ? <><td>{format(oldItem?.referenceMeanPercent, 0)} / {format(newItem?.referenceMeanPercent, 0)}</td><td>{format(oldItem?.ageMatchedPercent, 0)} / {format(newItem?.ageMatchedPercent, 0)}</td></> : null}
        <td><Delta comparison={change?.bmd} unit="" digits={3} /></td>
      </tr>;
    })}</tbody>
  </table></div>;
}

export function ComparisonReport({ comparison, includeOriginalPages = false }) {
  const current = comparison.currentRecord;
  const previous = comparison.previousRecord;
  const compositionScan = getVisual(current, "composition", "scan");
  const wholeBodyScan = getVisual(current, "wholeBodyBmd", "scan");
  const hipScan = getVisualFrom(current, [["hipLeft", "scan"], ["hipRight", "scan"], ["hip", "scan"]]);
  const lumbarScan = getVisualFrom(current, [["lumbar", "scan"], ["lumbarSummary", "scan"]]);
  const sourcePages = includeOriginalPages
    ? comparison.records.flatMap((record) => record.source.pages?.filter((page) => page.imageUrl) ?? [])
    : [];
  const hipRows = ["neck", "total"];
  const hipLabels = { neck: "Cuello femoral", total: "Cadera total" };
  const lumbarRows = ["l1", "l2", "l3", "l4", "total"];
  const lumbarLabels = { l1: "L1", l2: "L2", l3: "L3", l4: "L4", total: "L1-L4" };
  return <article id="patient-report" className="patient-report dxa-patient-report comparison-report">
    <section className="report-page dxa-report-page comparison-report-page">
      <Header comparison={comparison} page={1} />
      <div className="comparison-timeline"><strong>Periodo comparado</strong><span>{dateLabel(previous.exam.date)}</span><i /><span>{dateLabel(current.exam.date)}</span></div>
      <section className="dxa-report-section"><SectionBand icon={ScanLine} eyebrow="Cuantificación del tejido corporal total" title="Comparación de composición corporal" />
        <div className="comparison-composition-layout"><Scan src={compositionScan} title="Escaneo de la valoración actual" className="dxa-device-visual--body" /><TotalComparison comparison={comparison} /></div>
        <Interpretation title={comparison.transitions.fat} tone={comparison.current.headline.fat.status === "favorable" ? "green" : "orange"}>{comparison.summary}</Interpretation>
      </section>
      <section className="dxa-report-section dxa-report-section--compact"><SectionBand icon={Activity} eyebrow="Regiones de interés" title="Comparación de composición corporal regional" />
        <RegionalTable comparison={comparison} />
        <Interpretation title="Evolución regional y simetría">{comparison.symmetrySummary} Los cambios por región se presentan en la tabla para distinguir tejido graso, masa magra, contenido mineral óseo y masa total.</Interpretation>
      </section><Footer comparison={comparison} page={1} />
    </section>

    <section className="report-page dxa-report-page comparison-report-page">
      <Header comparison={comparison} page={2} />
      <section className="dxa-report-section"><SectionBand icon={Activity} eyebrow="Distribución del tejido graso" title="Comparación de índices adiposos" />
        <IndicesTable comparison={comparison} />
        <Interpretation title="Evolución de grasa y distribución" tone={comparison.current.adiposity.vat.status === "favorable" ? "green" : "orange"}>{comparison.adipositySummary}</Interpretation>
      </section>
      <section className="dxa-report-section"><SectionBand icon={Dumbbell} eyebrow="Índices de desarrollo muscular" title="Comparación de tejido magro" />
        <IndicesTable comparison={comparison} muscle />
        <Interpretation title="Evolución muscular" tone={comparison.current.muscle.status === "favorable" ? "green" : "orange"}>{comparison.muscleSummary}</Interpretation>
      </section><Footer comparison={comparison} page={2} />
    </section>

    <section className="report-page dxa-report-page comparison-report-page">
      <Header comparison={comparison} page={3} />
      <section className="dxa-report-section"><SectionBand icon={Bone} eyebrow="Cuerpo entero" title="Comparación de densidad mineral ósea por regiones" />
        <div className="comparison-bone-layout"><Scan src={wholeBodyScan} title="Escaneo óseo de la valoración actual" className="dxa-device-visual--bone" />
          <BoneComparisonTable labels={boneLabels} before={previous.bone.wholeBodyRegions} after={current.bone.wholeBodyRegions} comparisons={comparison.wholeBodyRegions} rows={Object.keys(boneLabels)} />
        </div>
        <div className="comparison-highlight-row"><div><span>DMO anterior</span><strong>{format(previous.bone.wholeBody.bmd, 3, " g/cm²")}</strong></div><div><span>DMO actual</span><strong>{format(current.bone.wholeBody.bmd, 3, " g/cm²")}</strong></div><div><span>Z-score anterior / actual</span><strong>{format(previous.bone.wholeBody.zScore, 1)} / {format(current.bone.wholeBody.zScore, 1)}</strong></div></div>
        <Interpretation title={comparison.transitions.bone} tone={comparison.current.bone.regionalRows.some((row) => row.status === "attention") ? "orange" : "green"}>{comparison.boneSummary} {comparison.current.bone.regionalRecommendation}</Interpretation>
      </section>
      <section className="dxa-report-section"><SectionBand icon={Bone} eyebrow={`Cadera ${current.bone.hip.side === "right" ? "derecha" : "izquierda"}`} title="Comparación de densidad mineral ósea de cadera" />
        <div className="comparison-site-layout"><Scan src={hipScan} title="Escaneo de cadera actual" className="dxa-device-visual--site" /><BoneComparisonTable labels={hipLabels} before={previous.bone.hip} after={current.bone.hip} comparisons={comparison.hip} rows={hipRows} includePercent /></div>
        <Interpretation title={`Cadera total · ${comparison.current.bone.mode === "t" ? "T" : "Z"}-score actual`}>{comparison.current.bone.hip.text} {comparison.hip.total.bmd.direction === "up" ? "La DMO aumentó frente a la valoración anterior." : comparison.hip.total.bmd.direction === "down" ? "La DMO disminuyó frente a la valoración anterior." : "La DMO se mantuvo estable."}</Interpretation>
      </section><Footer comparison={comparison} page={3} />
    </section>

    <section className="report-page dxa-report-page comparison-report-page">
      <Header comparison={comparison} page={4} />
      <section className="dxa-report-section"><SectionBand icon={Bone} eyebrow="Columna AP lumbar" title="Comparación de densidad mineral ósea de columna L1-L4" />
        <div className="comparison-site-layout"><Scan src={lumbarScan} title="Escaneo de columna lumbar actual" className="dxa-device-visual--lumbar" /><BoneComparisonTable labels={lumbarLabels} before={previous.bone.lumbar} after={current.bone.lumbar} comparisons={comparison.lumbar} rows={lumbarRows} includePercent /></div>
        <Interpretation title={`Columna L1-L4 · ${comparison.current.bone.mode === "t" ? "T" : "Z"}-score actual ${format(comparison.current.bone.mode === "t" ? current.bone.lumbar.total.tScore : current.bone.lumbar.total.zScore, 1)}`}>{comparison.current.bone.lumbar.text} {comparison.lumbar.total.bmd.direction === "up" ? "La DMO aumentó frente a la valoración anterior." : comparison.lumbar.total.bmd.direction === "down" ? "La DMO disminuyó frente a la valoración anterior." : "La DMO se mantuvo estable."}</Interpretation>
      </section>
      <section className="dxa-report-section dxa-report-section--conclusion"><SectionBand icon={FileCheck2} eyebrow="Lectura integrada del seguimiento" title="Conclusión interpretativa comparativa" />
        <div className="dxa-final-conclusion"><FileCheck2 /><p>{comparison.conclusion}</p></div>
        <div className="comparison-final-grid">
          {[
            ["Grasa corporal", previous.composition.total.fatPercent, current.composition.total.fatPercent, "%", 1],
            ["IMMA", previous.composition.indices.almi, current.composition.indices.almi, " kg/m²", 2],
            ["VAT", previous.composition.indices.vatAreaCm2, current.composition.indices.vatAreaCm2, " cm²", 1],
            ["DMO cadera", previous.bone.hip.total.bmd, current.bone.hip.total.bmd, " g/cm²", 3],
            ["DMO L1-L4", previous.bone.lumbar.total.bmd, current.bone.lumbar.total.bmd, " g/cm²", 3],
          ].map(([label, oldValue, newValue, unit, digits]) => <div key={label}><strong>{label}</strong><span>{dateLabel(previous.exam.date)}: {format(oldValue, digits, unit)}</span><span>{dateLabel(current.exam.date)}: {format(newValue, digits, unit)}</span></div>)}
        </div>
        <div className="dxa-professional-approval"><img src={firmaPaola} alt="Firma de la profesional responsable" /><span /><strong>Profesional: Nutricionista Paola Hoyos</strong><small>Centro de Ciencias del Deporte · Ministerio del Deporte</small></div>
      </section><Footer comparison={comparison} page={4} />
    </section>
    {sourcePages.map((page, index) => <section className="report-original-page" key={`${page.pageNumber}-${index}`}><img src={page.imageUrl} alt={`Página original ${page.pageNumber}`} /></section>)}
  </article>;
}
